"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getExpressionInferer = exports.inferExpression = void 0;
const common_1 = require("../types/common");
const builders_1 = require("../types/builders");
const substitution_1 = require("../substitution");
const unification_1 = require("../unification");
const util_1 = require("../../util");
const helpers_1 = require("./helpers");
function inferExpression(expression, context = {}) {
    const infer = getExpressionInferer();
    return infer(context, expression);
}
exports.inferExpression = inferExpression;
function getExpressionInferer(uniqueTypeVar = helpers_1.typeVarGenerator()) {
    function infer(context, expression) {
        switch (expression.kind) {
            case "Literal":
                return inferLiteral(context, expression);
            case "Identifier":
                return inferVariable(context, expression);
            case "Lambda":
                return inferLambda(context, expression);
            case "Let":
                return inferLet(context, expression);
            case "Application":
                return inferApplication(context, expression);
            case "Conditional":
                return inferConditional(context, expression);
            default:
                util_1.assertUnreachable(expression);
        }
    }
    function inferLiteral(context, literal) {
        switch (literal.value.kind) {
            case "Number":
                return { substitution: substitution_1.EMPTY_SUBSTITUTION, type: common_1.NUMBER_TYPE };
            case "Boolean":
                return { substitution: substitution_1.EMPTY_SUBSTITUTION, type: common_1.BOOL_TYPE };
            case "Character":
                return { substitution: substitution_1.EMPTY_SUBSTITUTION, type: common_1.CHARACTER_TYPE };
            case "BigInt":
                return { substitution: substitution_1.EMPTY_SUBSTITUTION, type: common_1.BIGINT_TYPE };
            case "Array":
                return inferArrayLiteral(literal.value);
            default:
                util_1.assertUnreachable(literal.value);
        }
        function inferArrayLiteral(literal) {
            const initialPreset = {
                substitution: substitution_1.EMPTY_SUBSTITUTION,
                type: uniqueTypeVar(),
            };
            const typeInfo = literal.contents.reduce((presetAcc, curr) => inferUsingPreset(presetAcc, context, curr), initialPreset);
            return {
                substitution: typeInfo.substitution,
                type: builders_1.arrayType(typeInfo.type),
            };
        }
    }
    function inferVariable(context, variable) {
        const scheme = context[variable.name];
        if (!scheme) {
            throw new helpers_1.NameError(`Name error: Unbound variable "${variable.name}"`);
        }
        return {
            substitution: substitution_1.EMPTY_SUBSTITUTION,
            type: helpers_1.instantiate(scheme, uniqueTypeVar),
        };
    }
    function inferLambda(context, { head, body }) {
        const headType = uniqueTypeVar();
        const temporaryContext = {
            ...context,
            [head]: builders_1.unboundScheme(headType),
        };
        const { substitution, type: bodyType } = infer(temporaryContext, body);
        return {
            substitution,
            type: builders_1.functionType(substitution_1.substituteInType(substitution, headType), bodyType),
        };
    }
    function inferLet(context, { variable, initializer, body }) {
        const { substitution: sub1, type: initializerType } = infer(context, initializer);
        const tempContext = {
            ...context,
            [variable]: builders_1.unboundScheme(initializerType),
        };
        const subbedContext = substitution_1.substituteInContext(sub1, tempContext);
        const { substitution: sub2, type: typeOfBody } = infer(subbedContext, body);
        return {
            substitution: substitution_1.composeSubstitutions(sub1, sub2),
            type: typeOfBody,
        };
    }
    function inferApplication(context, { callee, argument }) {
        const { substitution: s1, type: funcType } = infer(context, callee);
        const { substitution: s2, type: argType } = infer(substitution_1.substituteInContext(s1, context), argument);
        const resultType = uniqueTypeVar();
        const inferredType = builders_1.functionType(argType, resultType);
        const s3 = unification_1.unify(substitution_1.substituteInType(s2, funcType), inferredType);
        const composition = substitution_1.composeSubstitutions(s1, s2, s3);
        return {
            substitution: composition,
            type: substitution_1.substituteInType(s3, resultType),
        };
    }
    function inferConditional(context, conditional) {
        const { condition, thenBranch, elseBranch } = conditional;
        const { substitution: s1, type: condType } = infer(context, condition);
        const s2 = unification_1.unify(condType, common_1.BOOL_TYPE);
        const contextAfterCond = substitution_1.substituteInContext(substitution_1.composeSubstitutions(s1, s2), context);
        const { substitution: s3, type: branchType } = inferAndUnify(contextAfterCond, thenBranch, elseBranch);
        return {
            substitution: substitution_1.composeSubstitutions(s1, s2, s3),
            type: branchType,
        };
    }
    function inferAndUnify(context, e1, e2) {
        return inferUsingPreset(infer(context, e1), context, e2);
    }
    function inferUsingPreset(preset, context, expression) {
        const { substitution: s1, type: t1 } = preset;
        const { substitution: s2, type: t2 } = infer(substitution_1.substituteInContext(s1, context), expression);
        const s3 = unification_1.unify(t2, substitution_1.substituteInType(s2, t1));
        return {
            substitution: substitution_1.composeSubstitutions(s1, s2, s3),
            type: substitution_1.substituteInType(s3, t2),
        };
    }
    return infer;
}
exports.getExpressionInferer = getExpressionInferer;
//# sourceMappingURL=expressions.js.map